export interface TenantDocument {
  id: string;
  tenantInfoId: string;
  documentType: string;
  fileName: string;
  blobPath: string;
  uploadedAt: string;
  notes: string | null;
}

/** Les 11 types de documents locataire (backend TenantDocumentType). */
export const TENANT_DOCUMENT_TYPE_LABELS: Record<string, string> = {
  LEASE: 'Bail',
  INVENTORY_ENTRY: "État des lieux d'entrée",
  INVENTORY_EXIT: 'État des lieux de sortie',
  FURNITURE_INVENTORY: 'Inventaire du mobilier',
  GUARANTOR_ACT: 'Acte de cautionnement',
  INSURANCE_CERTIFICATE: "Attestation d'assurance",
  RIB: 'RIB',
  NOTICE_LETTER: 'Courrier de préavis',
  OTHER: 'Autre',
  ENROLLMENT_FILE: 'Pièce de candidature',
  GUARANTOR_SCHEME_CERTIFICATE: 'Attestation de garantie',
  LEASE_SIGNATURE_EVIDENCE: 'Dossier de preuve de signature',
};

/** Types téléversables manuellement (les autres sont produits par le système).
 *  Partagé par la page locataires du propriétaire et l'écran de dépôt agence :
 *  une agence dépose exactement les mêmes types qu'un propriétaire. */
export const UPLOADABLE_DOCUMENT_TYPES = [
  'LEASE',
  'INVENTORY_ENTRY',
  'INVENTORY_EXIT',
  'FURNITURE_INVENTORY',
  'GUARANTOR_ACT',
  'GUARANTOR_SCHEME_CERTIFICATE',
  'INSURANCE_CERTIFICATE',
  'RIB',
  'NOTICE_LETTER',
  'OTHER',
];

/** Vue agence d'un lot (chambre) : strictement minimisée côté backend.
 *  Ni loyer, ni charges, ni dépôt, ni email/téléphone, ni documents existants. */
export interface AgencyRoom {
  colocId: string;
  colocName: string | null;
  roomId: string;
  roomNumber: number | null;
  lotNumber: string | null;
  roomName: string;
  tenantInfoId: string | null;
  firstName: string | null;
  lastName: string | null;
  leaseStartDate: string | null;
  leaseEndDate: string | null;
  noticeDate: string | null;
  noticeExpectedExitDate: string | null;
  /** Ce qui MANQUE au dossier, jamais ce qu'il contient : une liste de courses,
   *  pas une divulgation. L'agence ne voit toujours aucune pièce. */
  dossierSuivi: boolean;
  dossierManques: string[];
  /** Le bail a-t-il commencé ? Sans lui la vue était binaire — un nom ou
   *  « libre » — et un lot réservé passait pour occupé. Aucune information
   *  nouvelle : c'est `leaseStartDate` comparée au jour même. */
  bailCommence: boolean;
}

export interface TenantDetail {
  id: string;
  userId: string;
  roomId: string | null;
  roomName: string | null;
  firstName: string;
  lastName: string;
  email: string;
  leaseStartDate: string | null;
  leaseEndDate: string | null;
  monthlyRent: number | null;
  chargesProvision: number | null;
  depositAmount: number | null;
  agencyExpenses: number | null;
  isActive: boolean;
  /** Accès du locataire à son espace, piloté par le propriétaire (≠ statut du bail). */
  accessEnabled: boolean;
  /** Calculés par le serveur, source unique. Faux le lendemain de l'état des
   *  lieux de sortie — le dossier, lui, reste actif six mois pour ses papiers. */
  occupeLaChambre: boolean;
  /** Faux avant le début du bail, si le propriétaire a coupé, et six mois
   *  après la sortie (fermeture automatique). */
  accesOuvert: boolean;
  /** Le message que le locataire reçoit quand son espace est fermé ; `null` s'il est ouvert. */
  motifAccesFerme: string | null;
  colocId: string | null;
  colocName: string | null;
  /** Réglage de la colocation : l'espace du locataire ne montre aucun montant. */
  showOnlyConsumption: boolean;
  noticeDate: string | null;
  /** Dates d'état des lieux : l'entrée ouvre l'accès, la sortie libère la chambre. */
  entryInventoryDate: string | null;
  exitInventoryDate: string | null;
  noticeExpectedExitDate: string | null;
  documents: TenantDocument[];
  /** `null` = non renseigné, PAS « pas de garant » : une partie des dossiers
   *  vient de l'agence, sans candidature d'où le déduire. */
  guarantorType: string | null;
  /** Faux tant que le locataire n'est pas entré dans les lieux : le dossier se
   *  construit et n'a rien à prouver. Distingue « complet » de « pas concerné ». */
  dossierSuivi: boolean;
  /** `types` : les types de pièce qui combleraient le manque, le premier étant
   *  celui à proposer au dépôt. Fournis par le serveur, source unique. */
  dossierManques: { code: string; label: string; types: string[] }[];
}

export interface TenantCheckInRequest {
  room_id: string;
  lease_start_date: string;
  monthly_rent: number;
  charges_provision?: number | null;
  // L'API les acceptait depuis toujours ; le type les omettait, et le
  // formulaire ne les demandait pas — ils restaient vides pour tout locataire
  // réservé depuis le back-office.
  deposit_amount?: number | null;
  agency_expenses?: number | null;
}

export interface TenantGiveNoticeRequest {
  notice_date: string;
  expected_exit_date: string;
}

export interface TenantCheckOutRequest {
  lease_end_date: string;
  /**
   * Date d'état des lieux de SORTIE — obligatoire. C'est elle qui fait foi :
   * la chambre redevient disponible le lendemain, et le locataire garde
   * l'accès à son dossier six mois de plus. Le préavis, lui, reste facultatif :
   * il n'annonce qu'une intention, l'état des lieux constate la remise des clés.
   */
  exit_inventory_date: string;
}

export interface TenantEntryInventoryRequest {
  entry_inventory_date: string;
}

export interface RoomSummary {
  id: string;
  colocId: string;
  name: string | null;
  surface: number | null;
  floor: number | null;
  // `GET /api/tenants/coloc/{id}/rooms` sérialise un `RoomResponse` complet :
  // ces deux champs arrivaient depuis toujours, le type les omettait. Ils
  // préremplissent le loyer et les charges à la réservation d'une chambre.
  priceNIC: number | null;
  charges: number | null;
  depositAmount: number | null;
  agencyExpenses: number | null;
  /** Fermée par le bailleur : jamais proposée à la réservation. */
  isClosed: boolean;
}

/**
 * `unassigned` : compte créé, aucune chambre — il attend qu'on lui en réserve une.
 * `reserved`   : chambre bloquée, bail non finalisé, accès encore fermé.
 */
export type TenantStatus = 'active' | 'reserved' | 'unassigned' | 'notice' | 'exited';

export const getTenantStatus = (tenant: TenantDetail): TenantStatus => {
  // Sans chambre, il n'est pas « sorti » — il n'est jamais entré. Le compte
  // vient d'être créé et attend qu'on lui réserve une chambre. Les afficher
  // tous deux « Sorti » mélangeait un ancien locataire et un futur.
  if (!tenant.roomId) return 'unassigned';
  // Sorti = la chambre est rendue, pas « dossier terminé » : la sortie laisse
  // le dossier actif six mois, « Sorti » n'était donc jamais atteint.
  if (!tenant.occupeLaChambre) return 'exited';
  // « Réservé » = le bail n'a pas encore commencé. La chambre lui est bloquée,
  // il arrive plus tard.
  //
  // C'était `!accessEnabled` jusqu'au 2026-08-31, ce qui nouait deux choses
  // sans rapport : l'accès dépendait du bail signé ET de l'état des lieux
  // d'entrée, deux pièces administratives qui arrivent souvent des semaines
  // après l'emménagement — un locataire installé n'avait donc pas ses charges.
  // Et couper l'accès de quelqu'un le faisait repasser « réservé » alors qu'il
  // occupe la chambre. `accessEnabled` n'est plus qu'un veto du propriétaire.
  return statutDuBail(tenant.noticeDate, tenant.leaseStartDate);
};

/**
 * Le cœur de la règle : préavis, réservé, actif.
 *
 * Extrait pour que l'espace AGENCE l'applique aussi. Il ne reçoit pas un
 * `TenantDetail` mais un lot, et recopier la comparaison de dates chez lui
 * aurait donné deux définitions du même statut — celles-là finissent toujours
 * par diverger.
 *
 * Comparaison à minuit des deux côtés : un bail qui commence aujourd'hui rend
 * le locataire actif dès aujourd'hui, pas à l'heure de sa signature.
 */
export const statutDuBail = (
  noticeDate: string | null,
  leaseStartDate: string | null,
): 'notice' | 'reserved' | 'active' => {
  if (noticeDate) return 'notice';
  if (!leaseStartDate) return 'reserved';
  const debut = new Date(leaseStartDate);
  const aujourdhui = new Date();
  debut.setHours(0, 0, 0, 0);
  aujourdhui.setHours(0, 0, 0, 0);
  return debut > aujourdhui ? 'reserved' : 'active';
};

export const getTenantStatusLabel = (status: TenantStatus): string => {
  switch (status) {
    // « En place » et non « Actif » : à côté d'un accès « Coupé », « Actif »
    // se lisait comme l'état du COMPTE. Ce badge dit la location — il habite
    // la chambre —, l'accès à l'espace a son propre badge.
    case 'active': return 'En place';
    case 'reserved': return 'Réservé';
    case 'unassigned': return 'À loger';
    case 'notice': return 'Préavis en cours';
    case 'exited': return 'Sorti';
  }
};



/**
 * L'accès du locataire à son espace, tel que le propriétaire doit le lire.
 *
 * Une seule définition pour la fiche ET la liste : deux copies de ce
 * branchement finiraient par dire « Ouvert » d'un côté et « Coupé » de l'autre.
 *
 * `accessEnabled` est un VETO du propriétaire, pas « il a emménagé » : l'espace
 * s'ouvre de lui-même au début du bail.
 */
export type EtatAcces = 'coupe' | 'attente' | 'ferme' | 'ouvert';

export const etatAcces = (tenant: TenantDetail): EtatAcces => {
  if (!tenant.accessEnabled) return 'coupe';
  const statut = getTenantStatus(tenant);
  if (statut === 'reserved') return 'attente';
  // Fermé de lui-même, six mois après la sortie.
  return statut === 'exited' && !tenant.accesOuvert ? 'ferme' : 'ouvert';
};

export const libelleAcces = (etat: EtatAcces): string =>
  etat === 'coupe' ? 'Coupé'
    : etat === 'attente' ? 'Au début du bail'
      : etat === 'ferme' ? 'Fermé'
        : 'Ouvert';

/** Une pièce déposée par l'agence elle-même (`GET /api/tenants/{id}/documents/mine`).
 *  Volontairement plus pauvre que `TenantDocument` : le backend ne renvoie pas
 *  le chemin de stockage, dont l'agence n'a aucun usage. */
export interface AgencyOwnDocument {
  id: string;
  documentType: string;
  fileName: string;
  uploadedAt: string | null;
  notes: string | null;
}


/** Types de garantie d'un dossier locataire.
 *
 *  « Non renseigné » n'est PAS « pas de garant » : tant que le type est vide,
 *  aucune pièce de garantie n'est réclamée au dossier. C'est ce qui évite de
 *  faire mentir l'indicateur pour les locataires arrivés par l'agence, dont
 *  personne ne sait s'ils relèvent de Visale ou d'un proche. */
export const GUARANTOR_TYPE_OPTIONS = [
  { value: '', label: 'Non renseigné' },
  { value: 'NONE', label: 'Aucun garant' },
  { value: 'INDIVIDUAL', label: 'Un proche se porte garant' },
  { value: 'INSTITUTIONAL', label: 'Garantie institutionnelle (Visale…)' },
];
